import { Component, Input } from '@angular/core';
import { STATE_BPPROFILES_CATALOG_OVERVIEW, STATE_BPPROFILES_OEM_OVERVIEW } from '../../../../../shared/app-states';
import { BPProfilesService, BPProfilesViewCode } from '../../../bp-profiles.service';
import { StateService } from '@uirouter/angular';
import { UntypedFormGroup } from '@angular/forms';
import { BaseBPProfilesDetailComponent } from '../../base-bp-profiles-detail.component';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '../../../../../shared/formly/ga-formly.service';
import { BPProfilesCatalogDataService, CATALOG_ACTION_TYPE_NEW, IOrderableItem, IOrderableItemResponse, ServiceCatalogType } from '../bp-profiles-catalog-data.service';
import { Override } from '../../../../../shared/app-decorators';
import { BPProfilesCatalogService } from '../bp-profiles-catalog.service';
import { util } from '../../../../../shared/util';
import { FormattingService } from '../../../../../shared/services/formatting.service';
import { GaStateService } from '../../../../../shared/services/ga-state.service';
import * as _ from 'underscore';
import { TranslationService } from '../../../../../shared/translation/translation.service';
import { Subscription } from 'rxjs';
import { GaValidateService } from '../../../../../shared/formly/ga-validate.service';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import { ILov } from '../../../../../../type_definitions/datatypes';

interface IOrderableItemModel extends IOrderableItem {
  preSelected?:boolean;
  preSelectedSelection?:IOrderableItem;
  defaultPrice?:number;
}

@Component({
  selector: 'bp-profiles-service-catalog-detail',
  template: `
    <button class="btn btn-primary margin-bottom-sm" (click)="backToOverview()"><i class="fa fa-chevron-left margin-right-sm"></i> <span translate [translateParams]="{ label: ('BPPROFILES.CATALOG.OVERVIEW' | translate) }">GA.BUTTONS.BACK_TO_LABEL</span></button>
    <formly [form]="form" [fields]="fields" [model]="model"></formly>
  `
})
export class BPProfilesCatalogDetailComponent extends BaseBPProfilesDetailComponent {
  @Input() public catalogItemId:string;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IOrderableItemModel;

  private onCancelSubscription:Subscription;
  private oemField:IExtraFieldConfigurationObject;
  private orderableUnitField:IExtraFieldConfigurationObject;
  private preselectedSelectionField:IExtraFieldConfigurationObject;
  private typeField:IExtraFieldConfigurationObject;
  private priceField:IExtraFieldConfigurationObject;
  private acslDefaultPriceField:IExtraFieldConfigurationObject;

  constructor(
    private validateService:GaValidateService,
    private translationService:TranslationService,
    private formattingService:FormattingService,
    private bpProfilesCatalogService:BPProfilesCatalogService,
    private bpProfilesCatalogDataService:BPProfilesCatalogDataService,
    private formlyService:GaFormlyService,
    private gaStateService:GaStateService,
    private authService:AuthService,
    protected bpProfilesService:BPProfilesService,
    protected stateService:StateService
  ) {
    super(bpProfilesService, stateService);
    // set the overview state
    this.overviewState = STATE_BPPROFILES_CATALOG_OVERVIEW;
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    if (!this.authService.viewWrite(BPProfilesViewCode.CATALOG)) {
      this.backToOverview();
    }

    // signal page in editing mode immediately
    this.bpProfilesService.editingMode = true;
    this.gaStateService.screen = 'BPPROFILES.CATALOG';
    this.model = util.copyAndExtend(this.originalCatalogItem);
    this.fields = this.createFields();

    // trigger 'change' for the first time, add timeout until formly is ready
    setTimeout(() => {
      this.onTypeChange(this.model.type);
    });

    this.fillFields();
    // register listener
    this.listen();
  }

  @Override(BaseBPProfilesDetailComponent)
  public listen():void {
    super.listen();
    this.onCancelSubscription = this.bpProfilesService.onCancel.subscribe(() => {
      this.backToOverview();
    });
  }

  @Override(BaseBPProfilesDetailComponent)
  public onEditChange():void {
    this.fields = this.createFields();
  }

  private get originalCatalogItem():IOrderableItem {
    if (this.isNewCatalogItem) {
      return {};
    }

    return _.findWhere(this.bpProfilesCatalogService.orderableItems, { id: Number(this.catalogItemId) });
  }

  private get isNewCatalogItem():boolean {
    return this.catalogItemId === CATALOG_ACTION_TYPE_NEW;
  }

  private get isFixedUnitType():boolean {
    if (this.isNewCatalogItem) {
      return this.model.preSelected && this.model.fixedUnitType;
    } else {
      return this.model.fixedUnitType;
    }
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-sm-3';
    const valueClass:string = 'col-sm-9';
    const smValueClass:string = 'col-sm-4';
    const fullWidthClass:string = 'col-sm-12';

    return [
      this.formlyService.aTemplate(`<h2>${this.model?.shortDescription}</h2>`)
        .withHideExpression(() => this.isNewCatalogItem)
        .build(),
      this.formlyService.aField(this.isNewCatalogItem? FormlyType.SELECT2 : FormlyType.VALUE, 'category')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatDescription)
        .withOptions(this.bpProfilesCatalogDataService.getPossibleCategories())
        .withExtraOptions({
          expressionProperties: {
            'templateOptions.disabled': ():boolean => !this.isNewCatalogItem
          }
        })
        .withTrackBy('name')
        .withRequired(this.isNewCatalogItem)
        .onChange(this.onCategoryChange)
        .build(),
      this.oemField = this.formlyService.aField(this.isNewCatalogItem? FormlyType.SELECT2 : FormlyType.VALUE, 'oem')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          expressionProperties: {
            'templateOptions.disabled': ():boolean => !this.isNewCatalogItem
          }
        })
        .withRequired(this.isNewCatalogItem)
        .onChange(this.loadPreselectedItems)
        .withGroupClass('margin-bottom-xs')
        .build(),
      this.formlyService.aLink(this.translationService.instant('GA.BUTTONS.ADD_LABEL', { label: 'BPPROFILES.OEM.NEW_OEM.TITLE' }))
        .withoutWrapper()
        .withWrapperClass('row')
        .withValueClass(`offset-sm-3 ${valueClass}`)
        .withExtraOptions({
          templateOptions: {
            uiSref: STATE_BPPROFILES_OEM_OVERVIEW
          }
        })
        .withHideExpression(() => !this.isNewCatalogItem)
        .build(),
      this.formlyService.aTemplate(`<div class="info-row margin-bottom-md description-info"><div class="info">${this.model?.description || ''}</div></div>`)
        .build(),
      this.formlyService.aRadio('preSelected')
        .isRequired()
        .withLabelClass(`form-label-big ${fullWidthClass}`)
        .withValueClass(fullWidthClass)
        .withOptions(this.bpProfilesCatalogDataService.getPreselectionOptions())
        .onChange(this.onPreselectedChange)
        .withLabelProp('label')
        .withValueProp('value')
        .withHideExpression(() => !this.isNewCatalogItem)
        .build(),
      this.preselectedSelectionField = this.formlyService.aSelect2('preSelectedSelection')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat((item:IOrderableItem) => item.shortDescription || item.description)
        .withHideExpression(() => !this.isNewCatalogItem || !this.model.preSelected)
        .withExtraOptions({
          expressionProperties: {
            'templateOptions.disabled': ():boolean => !this.model.category?.name && !this.model.oem?.id
          },
          templateOptions: {
            allowClear: true
          }
        })
        .onChange(this.onPreselectedSelectionChange)
        .build(),
      this.formlyService.anInput('part')
        .isRequired()
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withHideExpression(() => !this.isNewCatalogItem || util.isNullOrUndefinedOrEmpty(this.model.preSelected) || this.model.preSelected)
        .build(),
      this.formlyService.anInput('shortDescription')
        .isRequired()
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withHideExpression(() => !this.isNewCatalogItem || util.isNullOrUndefinedOrEmpty(this.model.preSelected) || this.model.preSelected)
        .build(),
      this.formlyService.aTextarea('description')
        .isRequired()
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withHideExpression(() => !this.isNewCatalogItem || util.isNullOrUndefinedOrEmpty(this.model.preSelected) || this.model.preSelected)
        .build(),
      this.typeField = this.formlyService.aRadio('type')
        .isRequired()
        .withLabelClass(`form-label-big ${fullWidthClass}`)
        .withValueClass(`margin-bottom-sm ${fullWidthClass}`)
        .withFormat(this.formattingService.formatLov)
        .withOptions(this.bpProfilesCatalogService.catalogTypes)
        .onChange(this.onTypeChange)
        .build(),
      this.formlyService.aTemplate(`<h3 class="margin-top-sm">${this.translationService.instant('BPPROFILES.CATALOG.PRICE.LABEL')}</h3>`)
        .build(),
      this.orderableUnitField = this.formlyService.aSelect2('unit')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(smValueClass)
        .withFormat(this.formattingService.formatDescription)
        .withHideExpression(() => this.isFixedUnitType)
        .withExtraOptions({
          templateOptions: {
            allowClear: true
          }
        })
        .build(),
      this.formlyService.aValue('unit')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatDescription)
        .withHideExpression(() => !this.isFixedUnitType)
        .build(),
      this.priceField = this.formlyService.anInput('price')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(smValueClass)
        .withInputType(FormlyType.NUMBER)
        .showInfo()
        .withAddOn(this.formattingService.formatCurrencyS(this.bpProfilesCatalogService.currency))
        .withExtraOptions({
          templateOptions: {
            min: 0
          }
        })
        .build(),
      this.acslDefaultPriceField = this.formlyService.aValue('defaultPrice')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat((value:number) => this.formattingService.formatAmount(value, this.bpProfilesCatalogService.currency))
        .withHideExpression(() => _.isUndefined(this.model.defaultPrice))
        .build(),
      this.formlyService.aTextarea('comments')
        .withLabelClass(`form-label-big ${fullWidthClass}`)
        .withValueClass(fullWidthClass)
        .build()
    ];
  }

  protected fillFields():void {
    if (this.isNewCatalogItem) {
      this.formlyService.fillField(this.oemField, this.bpProfilesCatalogDataService.getCatalogOEMs(this.bpProfilesService.activeProfile?.id));
    }
    this.loadOrderableUnitFieldOptions();
  }

  private loadOrderableUnitFieldOptions = ():void => {
    if (this.model.category?.name && !this.isFixedUnitType) {
      this.formlyService.fillField(this.orderableUnitField, this.bpProfilesCatalogDataService.getCatalogUnits(this.model.category?.name));
    }
  };

  private loadPreselectedItems = ():void => {
    if (this.isNewCatalogItem && this.model.category?.name && this.model.oem?.id && this.model.preSelected) {
      this.formlyService.fillField(
        this.preselectedSelectionField,
        this.bpProfilesCatalogDataService.getPreselectedItems(this.bpProfilesService.activeProfile?.id, this.model.category.name, this.model.oem.id)
          .then((res:IOrderableItemResponse) => {
            return res.items;
          })
      );
    }
  };

  private onCategoryChange = ():void => {
    this.orderableUnitField.formControl.reset();
    this.loadOrderableUnitFieldOptions();
    this.loadPreselectedItems();
  };

  private onPreselectedChange = ():void => {
    this.onPreselectedSelectionChange();
    this.loadPreselectedItems();
  };

  private onPreselectedSelectionChange = (preSelection?:IOrderableItem):void => {
    this.model.preSelectedSelection = preSelection;
    // show long description below field
    this.preselectedSelectionField.templateOptions.hint = preSelection?.description;
    // set preselection value because these fields are always exist no matter the preselected options
    this.typeField.formControl.setValue(preSelection?.type);
    this.priceField.formControl.setValue(preSelection?.price);
    this.acslDefaultPriceField.formControl.setValue(preSelection?.price);
    this.orderableUnitField.formControl.setValue(preSelection?.unit);

    const preSelectionModel:IOrderableItemModel = {
      part: preSelection?.part || preSelection?.globalPart,
      shortDescription: preSelection?.shortDescription,
      description: preSelection?.description,
      globalItemId: preSelection?.id,
      fixedUnitType: preSelection?.fixedUnitType
    };

    // extend model because some fields are not showing up in screen
    this.model = _.extend(this.model, preSelectionModel);
  };

  private onTypeChange = (type:ILov):void => {
    const isTypeNotAvailable:boolean = type?.id?.toString() === ServiceCatalogType.NOT_AVAILABLE;
    this.orderableUnitField.templateOptions.required = !isTypeNotAvailable;
    this.orderableUnitField.templateOptions.disabled = isTypeNotAvailable;

    this.priceField.templateOptions.required = !isTypeNotAvailable;
    this.priceField.templateOptions.disabled = isTypeNotAvailable;

    if (isTypeNotAvailable) {
      if (!this.isFixedUnitType) {
        this.orderableUnitField.formControl.reset();
      }
      this.priceField.formControl.reset();
    }
  };

  private createRequestData():IOrderableItem {
    const model:IOrderableItemModel = util.copyAndExtend(this.model);
    delete model.preSelectedSelection;
    delete model.preSelected;
    return model;
  }

  protected onSave = ():void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        let promise:Promise<IOrderableItem>;
        this.setSaveBusy(true);
        if (this.isNewCatalogItem) {
          promise = this.bpProfilesCatalogDataService.addCatalogItem(this.bpProfilesService.activeProfile?.id, this.createRequestData());
        } else {
          promise = this.bpProfilesCatalogDataService.updateCatalogItem(this.bpProfilesService.activeProfile?.id, this.createRequestData());
        }
        promise
          .then((data:IOrderableItem) => {
            // mark data to be highlighted in the grid
            this.bpProfilesCatalogService.highlightedId = data?.id;
            // reload catalog items
            return this.bpProfilesCatalogDataService.getCatalogItems(this.bpProfilesService.activeProfile?.id);
          })
          .then((orderableItemResponse:IOrderableItemResponse) => {
            this.bpProfilesCatalogService.currency = orderableItemResponse?.currency;
            this.bpProfilesCatalogService.orderableItems = orderableItemResponse?.items;
            this.form.markAsPristine();
            // after successfully reloaded catalog items then redirect to catalog overview
            this.backToOverview();
          })
          .finally(() => {
            this.setSaveBusy(false);
          });
      })
      .catch(util.noop);
  };

  @Override(BaseBPProfilesDetailComponent)
  public ngOnDestroy():void {
    super.ngOnDestroy();
    this.onCancelSubscription?.unsubscribe();
    this.gaStateService.screen = undefined;
    this.bpProfilesService.editingMode = false;
  }
}